"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  type PrismaColors,
  PrismaDiagram,
} from "@/components/tool-kit/diagrams/prisma-diagram";
import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { computePrisma, layoutPrisma } from "@/lib/tool-kit/diagrams/prisma";
import {
  PRISMA_EXAMPLE_COUNTS,
  PRISMA_WORKED_OUT,
  type PrismaCountKey,
  type PrismaListKey,
  prismaCheckText,
  prismaCountRows,
  readPrismaForm,
} from "@/lib/tool-kit/diagrams/prisma-form";

/** English strings. A translated content file overrides these through `ui`. */
export const PRISMA_FLOW_DIAGRAM_GENERATOR_STRINGS = {
  formHint:
    "Fill in the counts you have. A blank step is worked out from the step before it, and a number that does not add up is flagged below the form. Blank removals count as 0.",
  identificationTitle: "Identification",
  screeningTitle: "Screening",
  includedTitle: "Included",
  otherTitle: "Other methods",
  fieldDatabases: "Records from databases (total)",
  fieldDatabaseList:
    "Records from each database (optional) — one per line: name, n",
  fieldRegisters: "Records from registers (total)",
  fieldRegisterList:
    "Records from each register (optional) — one per line: name, n",
  fieldDuplicates: "Duplicate records removed",
  fieldAutomation: "Marked ineligible by automation tools",
  fieldOtherRemoved: "Removed for other reasons",
  fieldScreened: "Records screened",
  fieldExcluded: "Records excluded",
  fieldSought: "Reports sought for retrieval",
  fieldNotRetrieved: "Reports not retrieved",
  fieldAssessed: "Reports assessed for eligibility",
  fieldReasons: "Reports excluded, with reasons — one per line: reason, n",
  fieldStudies: "Studies included in review",
  fieldReports: "Reports of included studies",
  fieldOtherSources:
    "Records identified by other methods — one per line: source, n",
  calculated: "{value} (worked out)",
  otherToggle:
    "Add the “other methods” column: websites, organisations, citation searching",
  footnotesToggle: "Include the template's footnotes and source line",
  hideBlankToggle: "Leave out lines you left blank",
  colorsLabel: "Colors",
  colorsTemplate: "Template colors",
  colorsMono: "Black and white",
  exampleDatabases: "MEDLINE (Ovid), 812\nEmbase, 1054\nAPA PsycInfo, 391",
  exampleRegisters: "ClinicalTrials.gov, 31\nWHO ICTRP, 17",
  exampleReasons:
    "Wrong population, 31\nWrong intervention, 22\nWrong outcome, 17\nNot a randomized trial, 11",
  exampleOtherSources: "Websites, 9\nOrganisations, 3\nCitation searching, 14",
  exampleOtherReasons: "Wrong population, 4\nNo outcome data, 3",
  checksOk: "The numbers add up from identification to inclusion.",
  databaseTotal:
    "The databases listed add up to {sum}, not the {entered} entered as their total.",
  registerTotal:
    "The registers listed add up to {sum}, not the {entered} entered as their total.",
  mismatchScreened:
    "Records screened should be {expected} — records identified minus records removed before screening — not {entered}.",
  mismatchSought:
    "Reports sought for retrieval should be {expected} — records screened minus records excluded — not {entered}.",
  mismatchAssessed:
    "Reports assessed for eligibility should be {expected} — reports sought minus reports not retrieved — not {entered}.",
  mismatchOtherAssessed:
    "In the other-methods column, reports assessed should be {expected} — reports sought minus reports not retrieved — not {entered}.",
  mismatchReports:
    "Reports of included studies should be {expected} — reports assessed minus reports excluded, across both columns — not {entered}.",
  negative:
    "{box} comes out at {value}: more records leave the flow than enter it. Check the counts above it.",
  studiesOverReports:
    "Studies included ({studies}) cannot outnumber the reports of included studies ({reports}) — every included study has at least one report.",
  otherSoughtOverIdentified:
    "More reports are sought by other methods ({sought}) than records were identified ({identified}).",
  reasonsOverAssessed:
    "The exclusion reasons add up to {reasons}, more than the number of reports assessed ({assessed}).",
  reasonsWithoutNumber:
    "An exclusion reason has no number. Give every reason its count.",
  headerDatabases: "Identification of studies via databases and registers",
  headerOther: "Identification of studies via other methods",
  phaseIdentification: "Identification",
  phaseScreening: "Screening",
  phaseIncluded: "Included",
  identifiedFromMarked: "Records identified from*:",
  identifiedFrom: "Records identified from:",
  databases: "Databases",
  registers: "Registers",
  removedBefore: "Records removed before screening:",
  duplicates: "Duplicate records removed",
  automation: "Records marked as ineligible by automation tools",
  otherRemoved: "Records removed for other reasons",
  screened: "Records screened",
  excludedMarked: "Records excluded**",
  excluded: "Records excluded",
  sought: "Reports sought for retrieval",
  notRetrieved: "Reports not retrieved",
  assessed: "Reports assessed for eligibility",
  reportsExcluded: "Reports excluded:",
  studies: "Studies included in review",
  reports: "Reports of included studies",
  footnoteDatabases:
    "*Consider, if feasible to do so, reporting the number of records identified from each database or register searched (rather than the total number across all databases/registers).",
  footnoteAutomation:
    "**If automation tools were used, indicate how many records were excluded by a human and how many were excluded by automation tools.",
  source: "Source: Page MJ, et al. BMJ 2021;372:n71. doi: 10.1136/bmj.n71.",
  license:
    "This work is licensed under CC BY 4.0. To view a copy of this license, visit https://creativecommons.org/licenses/by/4.0/",
  nEquals: "(n = {n})",
  nBlank: "(n = )",
  boxColumn: "Box",
  countColumn: "n",
  otherPrefix: "Other methods: {box}",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout. Credit the template in the note.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy counts",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the counts as a table",
  chartLabel: "PRISMA 2020 flow diagram",
  chartDescription:
    "PRISMA 2020 flow diagram: {identified} records identified, {screened} screened, {assessed} reports assessed for eligibility, {studies} studies included.",
} as const;

type Strings = typeof PRISMA_FLOW_DIAGRAM_GENERATOR_STRINGS;

const FIELD_LABELS: Record<PrismaCountKey, keyof Strings> = {
  databases: "fieldDatabases",
  registers: "fieldRegisters",
  duplicates: "fieldDuplicates",
  automation: "fieldAutomation",
  otherRemoved: "fieldOtherRemoved",
  screened: "fieldScreened",
  excluded: "fieldExcluded",
  sought: "fieldSought",
  notRetrieved: "fieldNotRetrieved",
  assessed: "fieldAssessed",
  studies: "fieldStudies",
  reports: "fieldReports",
  otherSought: "fieldSought",
  otherNotRetrieved: "fieldNotRetrieved",
  otherAssessed: "fieldAssessed",
};

export default function PrismaFlowDiagramGeneratorWidget() {
  const strings = useToolUi(PRISMA_FLOW_DIAGRAM_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [counts, setCounts] = useState(PRISMA_EXAMPLE_COUNTS);
  const [lists, setLists] = useState<Record<PrismaListKey, string>>({
    databaseList: strings.exampleDatabases,
    registerList: strings.exampleRegisters,
    reasons: strings.exampleReasons,
    otherSources: strings.exampleOtherSources,
    otherReasons: strings.exampleOtherReasons,
  });
  const [other, setOther] = useState(true);
  const [footnotes, setFootnotes] = useState(true);
  const [hideBlank, setHideBlank] = useState(true);
  const [colors, setColors] = useState<PrismaColors>("template");
  const [style, update] = useChartStyle({
    title: "",
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const input = readPrismaForm(counts, lists, other);
  const { values, checks } = computePrisma(input);
  const nText = (value: number | null) =>
    value === null ? strings.nBlank : t(strings.nEquals, { n: fmt(value) });
  const layout = layoutPrisma(input, values, strings, {
    footnotes,
    hideBlank,
    title: style.apa ? "" : style.title,
    count: nText,
  });
  const reasonsUnnumbered = [
    ...input.reasons,
    ...(input.other?.reasons ?? []),
  ].some((reason) => reason.n === null);
  const messages = [
    ...checks.map((check) => prismaCheckText(check, strings, fmt)),
    ...(reasonsUnnumbered ? [strings.reasonsWithoutNumber] : []),
  ];
  const head = [strings.boxColumn, strings.countColumn];
  const rows = prismaCountRows(input, values, strings, fmt);
  const shown = (value: number | null) => (value === null ? "—" : fmt(value));

  const field = (key: PrismaCountKey) => {
    const worked = PRISMA_WORKED_OUT[key];
    const value =
      worked && values.calculated.has(worked) ? values[worked] : null;
    return (
      <TextField
        id={`pfd-${key}`}
        key={key}
        label={strings[FIELD_LABELS[key]]}
        numeric
        onChange={(text) => setCounts((old) => ({ ...old, [key]: text }))}
        placeholder={
          value === null
            ? undefined
            : t(strings.calculated, { value: fmt(value) })
        }
        value={counts[key]}
      />
    );
  };
  const list = (key: PrismaListKey, label: string) => (
    <TextAreaField
      id={`pfd-${key}`}
      label={label}
      onChange={(text) => setLists((old) => ({ ...old, [key]: text }))}
      prose
      rows={4}
      value={lists[key]}
    />
  );

  return (
    <div className="space-y-6">
      <p className={HINT}>{strings.formHint}</p>
      <Panel title={strings.identificationTitle}>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("databases")}
          {field("registers")}
          {list("databaseList", strings.fieldDatabaseList)}
          {list("registerList", strings.fieldRegisterList)}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {field("duplicates")}
          {field("automation")}
          {field("otherRemoved")}
        </div>
      </Panel>
      <Panel title={strings.screeningTitle}>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("screened")}
          {field("excluded")}
          {field("sought")}
          {field("notRetrieved")}
          {field("assessed")}
        </div>
        {list("reasons", strings.fieldReasons)}
      </Panel>
      <Panel title={strings.includedTitle}>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("studies")}
          {field("reports")}
        </div>
      </Panel>
      <Checkbox
        checked={other}
        label={strings.otherToggle}
        onChange={setOther}
      />
      {other ? (
        <Panel title={strings.otherTitle}>
          {list("otherSources", strings.fieldOtherSources)}
          <div className="grid gap-4 sm:grid-cols-3">
            {field("otherSought")}
            {field("otherNotRetrieved")}
            {field("otherAssessed")}
          </div>
          {list("otherReasons", strings.fieldReasons)}
        </Panel>
      ) : null}
      <div aria-live="polite" className="space-y-2">
        {messages.length === 0 ? (
          <p className="text-foreground text-sm">{strings.checksOk}</p>
        ) : (
          messages.map((message) => <Notice key={message}>{message}</Notice>)
        )}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <Checkbox
          checked={footnotes}
          label={strings.footnotesToggle}
          onChange={setFootnotes}
        />
        <Checkbox
          checked={hideBlank}
          label={strings.hideBlankToggle}
          onChange={setHideBlank}
        />
      </div>
      <PillGroup
        label={strings.colorsLabel}
        name="pfd-colors"
        onChange={setColors}
        options={[
          { value: "template", label: strings.colorsTemplate },
          { value: "mono", label: strings.colorsMono },
        ]}
        value={colors}
      />
      <ChartFigure
        apa={style.apa}
        data={toTsv([head, ...prismaCountRows(input, values, strings, String)])}
        figureNumber={style.figureNumber}
        filename={chartFilename(style.title, "prisma-flow-diagram")}
        labels={strings}
        note={style.note}
        table={{ head, rows }}
        title={style.title}
      >
        <PrismaDiagram
          colors={colors}
          description={t(strings.chartDescription, {
            identified: shown(values.identified),
            screened: shown(values.screened),
            assessed: shown(values.assessed),
            studies: shown(input.studies),
          })}
          id="pfd-diagram"
          label={style.title.trim() || strings.chartLabel}
          layout={layout}
        />
      </ChartFigure>
      <ChartStyleFields
        idPrefix="pfd"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
